import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "../lib/LangContext";
import { ArrowRight, Loader2 } from "lucide-react";

const ADMIN_WA = "6285220708569"; // Adtree Go admin WhatsApp
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegistrationForm = () => {
  const { t, lang } = useLang();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    whatsapp: "",
    category: "fnb",
    social_handle: "",
    followers_count: "",
  });

  const onChange = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.whatsapp || !form.category) {
      toast.error(lang === "id" ? "Lengkapi field wajib." : "Please fill required fields.");
      return;
    }

    setSubmitting(true);

    // 1) Save to database (non-blocking for WA — if DB fails, still open WA)
    try {
      const payload = {
        full_name: form.full_name,
        email: form.email,
        whatsapp: form.whatsapp,
        category: form.category,
        instagram_handle: form.social_handle || undefined,
        followers_count: form.followers_count ? Number(form.followers_count) : undefined,
      };
      Object.keys(payload).forEach(
        (k) => (payload[k] === undefined || payload[k] === "") && delete payload[k]
      );
      payload.full_name = form.full_name;
      payload.email = form.email;
      payload.whatsapp = form.whatsapp;
      payload.category = form.category;
      await axios.post(`${API}/registrations`, payload);
    } catch (err) {
      console.error("DB save failed", err);
      // Continue to WA anyway so user is not blocked
    }

    // 2) Build WhatsApp message
    const catLabel = t.register.categoryOptions[form.category];
    const lines =
      lang === "id"
        ? [
            "Halo Adtree Go! Saya ingin mendaftar sebagai creator:",
            "",
            `*Nama:* ${form.full_name}`,
            `*Email:* ${form.email}`,
            `*WhatsApp:* ${form.whatsapp}`,
            `*Kategori:* ${catLabel}`,
            form.social_handle && `*Instagram / TikTok:* ${form.social_handle}`,
            form.followers_count && `*Followers:* ${form.followers_count}`,
            "",
            "Terima kasih!",
          ]
        : [
            "Hi Adtree Go! I'd like to register as a creator:",
            "",
            `*Name:* ${form.full_name}`,
            `*Email:* ${form.email}`,
            `*WhatsApp:* ${form.whatsapp}`,
            `*Category:* ${catLabel}`,
            form.social_handle && `*Instagram / TikTok:* ${form.social_handle}`,
            form.followers_count && `*Followers:* ${form.followers_count}`,
            "",
            "Thank you!",
          ];

    const message = encodeURIComponent(lines.filter(Boolean).join("\n"));
    const url = `https://wa.me/${ADMIN_WA}?text=${message}`;
    window.open(url, "_blank", "noopener,noreferrer");
    toast.success(t.register.success);
    setForm({
      full_name: "",
      email: "",
      whatsapp: "",
      category: "fnb",
      social_handle: "",
      followers_count: "",
    });
    setSubmitting(false);
  };

  const L = t.register.labels;
  const opts = t.register.categoryOptions;

  return (
    <section id="register" data-testid="register-section" className="relative bg-black py-20 md:py-28">
      <div className="max-w-screen-2xl mx-auto px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left col */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="h-px w-8 bg-brand-yellow" />
              <span className="text-[11px] uppercase tracking-[0.4em] text-brand-yellow font-semibold">
                {t.register.eyebrow}
              </span>
            </div>
            <h2 className="font-anton uppercase text-white leading-[0.95] mb-5" style={{ fontSize: "clamp(2rem, 4.5vw, 3.75rem)" }}>
              {t.register.title}
            </h2>
            <p className="text-white/60 leading-relaxed text-sm md:text-base max-w-md">{t.register.desc}</p>

            {/* Voucher motif card */}
            <div className="mt-10 ticket bg-brand-yellow text-black p-6 md:p-8 max-w-sm">
              <div className="text-[10px] uppercase tracking-[0.4em] font-bold opacity-80">
                Adtree Go · Creator
              </div>
              <div className="font-anton text-3xl md:text-4xl uppercase mt-2 leading-tight">
                {lang === "id" ? "Voucher Pendaftaran" : "Registration Voucher"}
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs uppercase tracking-widest opacity-70 border-t border-black/20 pt-3">
                <span>★ ★ ★ ★ ★</span>
                <span className="ml-auto">2026 / ID</span>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form onSubmit={onSubmit} data-testid="registration-form" className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                <Field label={L.full_name} required>
                  <input
                    data-testid="input-full-name"
                    className="input-line"
                    placeholder="John Doe"
                    value={form.full_name}
                    onChange={onChange("full_name")}
                    required
                  />
                </Field>
                <Field label={L.email} required>
                  <input
                    data-testid="input-email"
                    type="email"
                    className="input-line"
                    placeholder="you@email.com"
                    value={form.email}
                    onChange={onChange("email")}
                    required
                  />
                </Field>
                <Field label={L.whatsapp} required>
                  <input
                    data-testid="input-whatsapp"
                    className="input-line"
                    placeholder="+62 812 ..."
                    value={form.whatsapp}
                    onChange={onChange("whatsapp")}
                    required
                  />
                </Field>
                <Field label={L.category} required>
                  <div className="relative">
                    <select
                      data-testid="input-category"
                      className="input-line appearance-none pr-8"
                      value={form.category}
                      onChange={onChange("category")}
                    >
                      <option value="fnb" className="bg-black">{opts.fnb}</option>
                      <option value="accommodation" className="bg-black">{opts.accommodation}</option>
                      <option value="both" className="bg-black">{opts.both}</option>
                    </select>
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-brand-yellow text-xs">▼</span>
                  </div>
                </Field>
                <Field label={L.social_handle}>
                  <input
                    data-testid="input-social"
                    className="input-line"
                    placeholder="@username"
                    value={form.social_handle}
                    onChange={onChange("social_handle")}
                  />
                </Field>
                <Field label={L.followers_count}>
                  <input
                    data-testid="input-followers"
                    type="number"
                    min="0"
                    className="input-line"
                    placeholder="50000"
                    value={form.followers_count}
                    onChange={onChange("followers_count")}
                  />
                </Field>
              </div>

              <div className="pt-4">
                <button
                  data-testid="submit-registration"
                  type="submit"
                  disabled={submitting}
                  className="group inline-flex items-center justify-center gap-3 bg-brand-yellow text-black font-anton uppercase tracking-widest text-sm md:text-base px-8 py-4 hover:bg-white transition-all disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {t.cta.sending}
                    </>
                  ) : (
                    <>
                      {t.cta.submitWa}
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </button>
                <p className="text-xs text-white/40 mt-4">
                  {lang === "id"
                    ? "Setelah klik, WhatsApp akan terbuka dengan pesan otomatis ke admin Adtree Go."
                    : "After clicking, WhatsApp will open with an auto‑filled message to the Adtree Go admin."}
                </p>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, required, children }) => (
  <label className="block">
    <span className="block text-[10px] uppercase tracking-[0.3em] text-white/50 mb-1">
      {label}
      {required && <span className="text-brand-yellow ml-1">*</span>}
    </span>
    {children}
  </label>
);

export default RegistrationForm;
